if (!customElements.get("cro-smart-sticky-atc")) {
  customElements.define(
    "cro-smart-sticky-atc",
    class CroSmartStickyAtc extends HTMLElement {
      connectedCallback() {
        this.productSection = Array.from(
          document.querySelectorAll(
            '[data-section-type="product-page"][data-section-id]',
          ),
        ).find(
          (section) => section.dataset.sectionId === this.dataset.sectionId,
        );
        this.nativeForm =
          this.productSection &&
          this.productSection.querySelector(".m-product-form--main");
        this.nativeButton =
          this.nativeForm && this.nativeForm.querySelector(".m-add-to-cart");
        this.variantPicker =
          this.productSection &&
          this.productSection.querySelector("variant-picker");
        this.button = this.querySelector(".cro-smart-sticky-atc__button");
        this.buttonText =
          this.button && this.button.querySelector(".m-add-to-cart--text");
        this.pricing = this.querySelector(".cro-smart-sticky-atc__pricing");
        this.priceMirror =
          this.pricing &&
          (this.pricing.shadowRoot ||
            this.pricing.attachShadow({ mode: "open" }));
        this.mainProductForm =
          this.nativeForm && this.nativeForm.tagName === "PRODUCT-FORM"
            ? this.nativeForm
            : this.nativeForm && this.nativeForm.closest("product-form");
        this.isSubmitting = false;
        this.variants = this.getVariants();
        this.isDesktop = window.matchMedia("(min-width: 768px)");
        this.updateVisibility = this.updateVisibility.bind(this);
        this.initializeStickyState = this.initializeStickyState.bind(this);
        this.mirrorPriceBlock = this.mirrorPriceBlock.bind(this);

        if (
          !this.nativeButton ||
          !this.variantPicker ||
          !this.button ||
          !this.pricing
        )
          return;

        this.sizePicker = Array.from(
          this.variantPicker.querySelectorAll("[data-picker-field]"),
        ).find((picker) =>
          picker.dataset.optionName.toLowerCase().includes("size"),
        );

        this.button.addEventListener("click", () => this.handleAddToCart());
        this.sizePicker &&
          this.sizePicker.addEventListener("change", () => {
            this.clearSizeError();
            this.updateButtonState(this.getCurrentVariant());
          });
        window.addEventListener("scroll", this.updateVisibility, {
          passive: true,
        });
        window.addEventListener("resize", this.updateVisibility);
        this.isDesktop.addEventListener("change", this.updateVisibility);

        this.observer = new IntersectionObserver(this.updateVisibility, {
          threshold: [0, 0.01, 1],
        });
        this.setupNativeAtcObservers();
        this.nativeAtcDomObserver = new MutationObserver(() => {
          if (this.nativeAtcObserverTimer)
            window.clearTimeout(this.nativeAtcObserverTimer);
          this.nativeAtcObserverTimer = window.setTimeout(() => {
            this.setupNativeAtcObservers();
            this.setupMainPriceObserver();
            this.updateVisibility();
          }, 100);
        });
        this.nativeAtcDomObserver.observe(this.productSection, {
          childList: true,
          subtree: true,
        });

        this.setupMainPriceObserver();

        if (this.mainProductForm) {
          this.formLoadObserver = new MutationObserver(() => {
            this.syncSubmittingState(
              this.mainProductForm.classList.contains("m-spinner-loading"),
            );
          });
          this.formLoadObserver.observe(this.mainProductForm, {
            attributes: true,
            attributeFilter: ["class"],
          });
        }

        customElements.whenDefined("variant-picker").then(() => {
          requestAnimationFrame(() => {
            this.initializeStickyState();
            this.mirrorPriceBlock();
          });
        });

        if (window.MinimogEvents) {
          window.MinimogEvents.subscribe(
            `${this.dataset.productId}__VARIANT_CHANGE`,
            () => {
              requestAnimationFrame(() => {
                this.setupMainPriceObserver();
                this.updateButtonState(this.getCurrentVariant());
              });
            },
          );
        }
      }

      disconnectedCallback() {
        window.removeEventListener("scroll", this.updateVisibility);
        window.removeEventListener("resize", this.updateVisibility);
        this.isDesktop &&
          this.isDesktop.removeEventListener("change", this.updateVisibility);
        this.observer && this.observer.disconnect();
        this.formLoadObserver && this.formLoadObserver.disconnect();
        this.nativeAtcDomObserver && this.nativeAtcDomObserver.disconnect();
        this.mainPriceObserver && this.mainPriceObserver.disconnect();
        if (this.nativeAtcObserverTimer)
          window.clearTimeout(this.nativeAtcObserverTimer);
      }

      getMainPriceBlock() {
        if (!this.productSection) return null;

        return this.productSection.querySelector(".product-block-group--price");
      }

      setupMainPriceObserver() {
        const priceBlock = this.getMainPriceBlock();
        if (!priceBlock) return;

        if (this.mainPriceBlock !== priceBlock) {
          this.mainPriceObserver && this.mainPriceObserver.disconnect();
          this.mainPriceBlock = priceBlock;
          this.mainPriceObserver = new MutationObserver(this.mirrorPriceBlock);
          this.mainPriceObserver.observe(priceBlock, {
            childList: true,
            subtree: true,
            characterData: true,
            attributes: true,
          });
        }

        this.mirrorPriceBlock();
      }

      mirrorPriceBlock() {
        if (!this.priceMirror || !this.mainPriceBlock) return;

        const sourcePrice = this.mainPriceBlock.querySelector(".m-price");
        if (!sourcePrice) return;

        const clone = sourcePrice.cloneNode(true);
        const sourceElements = [
          sourcePrice,
          ...sourcePrice.querySelectorAll("*"),
        ];
        const cloneElements = [clone, ...clone.querySelectorAll("*")];

        sourceElements.forEach((source, index) => {
          this.copyVisualStyles(source, cloneElements[index]);
        });

        clone
          .querySelectorAll(
            [
              ".m-price__unit-wrapper",
              ".m-price__availability",
              ".m-price__badge-sold-out",
              '[class*="visually-hidden"]',
            ].join(", "),
          )
          .forEach((element) => element.remove());

        Object.assign(clone.style, {
          display: "inline-flex",
          alignItems: "baseline",
          flexDirection: "row",
          flexWrap: "nowrap",
          gap: "8px",
          margin: "0",
          whiteSpace: "nowrap",
        });

        const sale = clone.querySelector(".m-price__sale");
        const sourceSale = sourcePrice.querySelector(".m-price__sale");
        if (
          sale &&
          sourceSale &&
          window.getComputedStyle(sourceSale).display !== "none"
        ) {
          Object.assign(sale.style, {
            display: "inline-flex",
            alignItems: "baseline",
            flexDirection: "row",
            flexWrap: "nowrap",
            gap: "8px",
          });
        }

        const badges = clone.querySelector(".m-price__badges");
        if (badges) badges.style.alignSelf = "center";

        this.applyStickyPriceStyles(clone);
        this.priceMirror.replaceChildren(clone);
      }

      applyStickyPriceStyles(price) {
        price
          .querySelectorAll(
            ".m-price__regular .m-price-item--regular, .m-price-item--sale",
          )
          .forEach((element) => {
            Object.assign(element.style, {
              color: "rgb(232, 78, 78)",
              background: "transparent",
              border: "0",
              borderRadius: "0",
              padding: "0",
              margin: "0",
              fontFamily: "Poppins, sans-serif",
              fontSize: "21px",
              fontStyle: "normal",
              fontWeight: "400",
              letterSpacing: "normal",
              lineHeight: "28px",
              textDecoration: "none",
              textTransform: "none",
              whiteSpace: "normal",
              opacity: "1",
            });
          });

        price
          .querySelectorAll(".m-price__sale .m-price-item--regular")
          .forEach((element) => {
            Object.assign(element.style, {
              color: "rgb(102, 102, 102)",
              background: "transparent",
              border: "0",
              borderRadius: "0",
              padding: "0",
              margin: "0",
              fontFamily: "Poppins, sans-serif",
              fontSize: "12.25px",
              fontStyle: "normal",
              fontWeight: "400",
              letterSpacing: "normal",
              lineHeight: "18.375px",
              textDecoration: "line-through",
              textTransform: "none",
              whiteSpace: "normal",
              opacity: "1",
            });
          });

        price.querySelectorAll(".m-product-tag--sale").forEach((element) => {
          Object.assign(element.style, {
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "normal",
            flexFlow: "row",
            gap: "normal",
            color: "rgb(255, 255, 255)",
            background: "rgb(218, 63, 63)",
            border: "0",
            borderRadius: "11px",
            padding: "2px 10px",
            margin: "0",
            fontFamily: "Poppins, sans-serif",
            fontSize: "11px",
            fontStyle: "normal",
            fontWeight: "600",
            letterSpacing: "normal",
            lineHeight: "16px",
            textDecoration: "none",
            textTransform: "uppercase",
            whiteSpace: "normal",
            opacity: "1",
          });
        });
      }

      copyVisualStyles(source, clone) {
        if (!source || !clone) return;

        const computed = window.getComputedStyle(source);
        [
          "display",
          "align-items",
          "justify-content",
          "flex-direction",
          "flex-wrap",
          "gap",
          "color",
          "background",
          "border",
          "border-radius",
          "padding",
          "margin",
          "font-family",
          "font-size",
          "font-style",
          "font-weight",
          "letter-spacing",
          "line-height",
          "text-decoration",
          "text-transform",
          "white-space",
          "opacity",
        ].forEach((property) => {
          clone.style.setProperty(property, computed.getPropertyValue(property));
        });
      }

      getNativeAtcButtons() {
        if (!this.productSection) return [];

        return Array.from(
          this.productSection.querySelectorAll('.m-add-to-cart[name="add"]'),
        ).filter(
          (button) =>
            !button.closest("cro-smart-sticky-atc") &&
            !button.closest(".m-sticky-addtocart"),
        );
      }

      getReviewAtcButtons() {
        return Array.from(
          document.querySelectorAll(".real-review-section .new-btn"),
        );
      }

      setupNativeAtcObservers() {
        if (!this.observer) return;

        this.observer.disconnect();
        [...this.getNativeAtcButtons(), ...this.getReviewAtcButtons()].forEach(
          (button) => this.observer.observe(button),
        );
      }

      isAnyReviewAtcVisible() {
        return this.getReviewAtcButtons().some((button) => {
          const rect = button.getBoundingClientRect();
          return rect.bottom > 0 && rect.top < window.innerHeight;
        });
      }

      isAnyNativeAtcVisible() {
        return this.getNativeAtcButtons().some((button) => {
          const rect = button.getBoundingClientRect();
          return rect.bottom > 0 && rect.top < window.innerHeight;
        });
      }

      getVariants() {
        const data = this.querySelector("[data-cro-sticky-variants]");
        if (!data) return [];

        try {
          return JSON.parse(data.textContent);
        } catch (error) {
          console.error("Unable to parse CRO sticky ATC variants.", error);
          return [];
        }
      }

      updateVisibility() {
        if (!this.nativeButton) return;

        const nativeButtonRect = this.nativeButton.getBoundingClientRect();
        const hasScrolledPastNativeButton = nativeButtonRect.bottom <= 0;
        const isReviewAtcVisible = this.isAnyReviewAtcVisible();
        const shouldShow = this.isDesktop.matches
          ? hasScrolledPastNativeButton && !isReviewAtcVisible
          : !this.isAnyNativeAtcVisible() && !isReviewAtcVisible;

        this.classList.toggle("is-visible", shouldShow);
        this.setAttribute("aria-hidden", shouldShow ? "false" : "true");
      }

      initializeStickyState() {
        if (
          !this.selectionReset &&
          this.sizePicker &&
          !window.location.search.includes("?variant=") &&
          this.variants.length > 1 &&
          typeof this.variantPicker.disableSelectedVariantDefault === "function"
        ) {
          this.variantPicker.disableSelectedVariantDefault();
          this.selectionReset = true;
        }

        this.mirrorPriceBlock();
        this.updateButtonState(this.getCurrentVariant());
        this.updateVisibility();
      }

      hasSelectedSize() {
        if (!this.sizePicker) return true;

        const select = this.sizePicker.querySelector("select");
        if (select) return Boolean(select.value);

        return Boolean(
          this.sizePicker.querySelector('input[type="radio"]:checked'),
        );
      }

      getCurrentVariant() {
        const variantId = this.nativeForm.querySelector('[name="id"]');
        if (!variantId || !variantId.value) return null;
        return (
          this.variants.find((item) => item.id === Number(variantId.value)) ||
          null
        );
      }

      getAddToCartLabel() {
        const strings = window.MinimogStrings || {};
        return (strings.addToCart || "Add to cart").toUpperCase();
      }

      getSoldOutLabel() {
        const strings = window.MinimogStrings || {};
        return (strings.soldOut || "Sold out").toUpperCase();
      }

      syncSubmittingState(isSubmitting) {
        this.isSubmitting = isSubmitting;
        if (!this.button) return;

        this.button.classList.toggle("m-spinner-loading", isSubmitting);

        if (isSubmitting) {
          this.button.disabled = true;
          this.button.setAttribute("aria-disabled", "true");
          return;
        }

        this.updateButtonState(this.getCurrentVariant());
      }

      handleAddToCart() {
        if (
          this.isSubmitting ||
          (this.mainProductForm &&
            this.mainProductForm.classList.contains("m-spinner-loading"))
        ) {
          return;
        }

        if (!this.hasSelectedSize()) {
          this.showSizeError();
          return;
        }

        const variant = this.getCurrentVariant();
        if (!variant || !variant.available) return;

        this.syncSubmittingState(true);
        this.nativeButton.click();

        requestAnimationFrame(() => {
          if (
            this.mainProductForm &&
            !this.mainProductForm.classList.contains("m-spinner-loading")
          ) {
            this.syncSubmittingState(false);
          }
        });
      }

      showSizeError() {
        if (!this.sizePicker) return;

        const option = this.sizePicker.closest(".m-product-option");
        const error = option && option.querySelector(".m-product-option-error");
        option && option.classList.add("m-product-option--needs-selection");

        if (error) {
          error.classList.add("cro-size-selection-message");
          error.setAttribute("role", "alert");
          error.textContent = "Select your size";
        }

        this.sizePicker.setAttribute("tabindex", "-1");
        this.sizePicker.scrollIntoView({ behavior: "smooth", block: "center" });
        this.sizePicker.focus({ preventScroll: true });
      }

      clearSizeError() {
        if (!this.sizePicker) return;

        const option = this.sizePicker.closest(".m-product-option");
        const error = option && option.querySelector(".m-product-option-error");
        option && option.classList.remove("m-product-option--needs-selection");

        if (error) {
          error.classList.remove("cro-size-selection-message");
          error.removeAttribute("role");
          error.textContent = "";
        }
      }

      updateButtonState(variant) {
        if (!this.button || this.isSubmitting) return;

        const hasSize = this.hasSelectedSize();
        const resolvedVariant = hasSize ? variant : null;
        const soldOut = Boolean(resolvedVariant && !resolvedVariant.available);
        const label = soldOut
          ? this.getSoldOutLabel()
          : this.getAddToCartLabel();

        if (this.buttonText) {
          this.buttonText.textContent = label;
        } else {
          this.button.textContent = label;
        }

        this.button.disabled = soldOut;
        this.button.classList.toggle("is-sold-out", soldOut);
        this.button.classList.toggle("disabled", soldOut);
        this.button.setAttribute("aria-disabled", soldOut ? "true" : "false");
      }
    },
  );
}
